import { HTTPService } from "../app/core/util/httpManager.js";
import { General } from "./general.js";
import { Helper } from "../app/core/util/helper.js";
import { Drawer } from "./drawer.js";
import { Sheet, SheetActionSignaling, SheetEventType, SheetAfterInteractionEventType } from "./sheet.js";
import { Config } from "../app/core/config/config.js";

export class SheetManager {
    private _sheets: Array<Sheet> = [];
    private readonly _hiddenContainer: JQuery;
    private readonly _sheetContainer: JQuery;
    private _leftDrawer: Drawer;
    private _sheetActionHandlers: Map<string, Array<Function>>;
    //private _rightDrawer: Drawer


    constructor(config: { sheetContainer: JQuery, hiddenContainer: JQuery, leftDrawer: Drawer/*, rightDrawer: Drawer */ }) {
        this._sheetContainer = config.sheetContainer;
        this._hiddenContainer = config.hiddenContainer;
        this._leftDrawer = config.leftDrawer;
        this._sheetActionHandlers = new Map();
        //this._rightDrawer = config.rightDrawer;
    }


    private _listener: SheetActionSignaling = (action: string, data: any) => {
        switch (action) {
            case "remove":
                const sheet = data as Sheet;
                let idx = -1;
                for (let i = 0; i < this._sheets.length; i++) {
                    if (this._sheets[i] === sheet) {
                        idx = i;
                        break;
                    }
                }
                if (idx > -1) {
                    this._sheets.splice(idx, 1);
                }
                break;
            default:
                break;
        }
        const handlers = this._sheetActionHandlers.get(action);
        if (Array.isArray(handlers)) {
            handlers.forEach((func) => {
                func();
            });
        }
    }


    createSheetWithUrl = (viewUrl: string, data?: any, externalData?: any, revertTabTitleOnClose = false) => {
        const tabTitle = document.title;
        let url = viewUrl;
        if (url.indexOf(Config.baseUrl) === 0) {
            url = url.substr(Config.baseUrl.length);
        }
        const sheet = this.createSheet(null, true);
        sheet.awaitsComponentInitialization(true);
        sheet.setSourceUrl(encodeURIComponent(url));
        if (Helper.hasValue(externalData)) {
            sheet.setExternalData(externalData);
        }

        HTTPService.get(url, data)
            .then(result => {
                sheet.show(() => {
                    const hidden = General.Content.AppendHiddenContent(result);
                    sheet.bind(SheetEventType.Close, () => {
                        hidden.remove();
                    });
                })
            });
        if (revertTabTitleOnClose) {
            sheet.bindAfterInteraction(SheetAfterInteractionEventType.AfterHide, () => {
                document.title = tabTitle;
            });
        }
        return sheet;
    }

    createSheet = (existingElement?: JQuery, createHidden?: boolean, externalData?: any): Sheet => {
        const leftDrawerContent = this._leftDrawer.createContentElement();

        const sheet = new Sheet({ leftDrawerContent: leftDrawerContent, signalHandler: this._listener, existingWrapper: existingElement, noAnimation: this._sheets.length === 0 });
        sheet.setHiddenWrapperElement(this._hiddenContainer);
        sheet.setParentElement(this._sheetContainer);
        if (Helper.hasValue(externalData)) {
            sheet.setExternalData(externalData);
        }

        if ((this._sheets.length === 0 || (Helper.hasValue(existingElement) && existingElement.length > 0)) && createHidden !== true) {
            sheet.show();
        } else {
            sheet.hide(null, true);
        }

        this._sheets.push(sheet);
        return sheet;
    }

    getTopMostSheet = () => {
        if (this._sheets.length === 0) {
            return null;
        } else {
            return this._sheets[this._sheets.length - 1];
        }
    }

    getTopMostVisibleSheet = () => {
        for (let i = this._sheets.length - 1; i >= 0; i--) {
            if (!this._sheets[i].isHidden()) {
                return this._sheets[i];
            }
        }
        return null;
    }

    getSheetByIndex = (index: number): Sheet => {
        return this._sheets[index];
    }

    getSheetById = (guid: string) => {
        const filtered = this._sheets.filter(x => x.id() === guid);

        return filtered.length === 1 ? filtered[0] : null;
    }

    getSheetsCount = () => {
        return this._sheets.length;
    }

    getSheetIndex = (guid: string) => {
        for (let i = 0; i < this._sheets.length; i++) {
            if (this._sheets[i].id() === guid) {
                return i;
            }
        }

        return -1;
    }

    getVisibleSheetsCount = () => {
        return this._sheets.filter(x => !x.isHidden()).length;
    }

    cascadeRemoveSheets = (lastSheetGuid: string, afterClose?: Function) => {
        let idx = -1;
        for (let i = 0; i < this._sheets.length; i++) {
            if (this._sheets[i].id() === lastSheetGuid) {
                //hidden? cached?...
                idx = i;
                break;
            }
        }

        if (idx > -1) {
            //i > idx => the first (target) sheet cannot be filtered
            const sheets = this._sheets.filter((x, i) => i > idx && x.prepareForCascadeRemove());

            let closeFunc = () => {
                this._sheets[idx].remove(afterClose);
            };

            sheets.forEach((sheet) => {
                closeFunc = ((s: Sheet, f: Function) => {
                    return () => {
                        s.remove(f);
                    }
                })(sheet, closeFunc);
            });

            closeFunc();
        }
    }

    forcedRemoveSheetsNoAnimation = (lastSheetGuid: string) => {
        let idx = -1;
        for (let i = 0; i < this._sheets.length; i++) {
            if (this._sheets[i].id() === lastSheetGuid) {
                //hidden? cached?...
                idx = i;
                break;
            }
        }

        if (idx > -1) {
            //i > idx => the first (target) sheet cannot be filtered
            const sheets = this._sheets.filter((x, i) => i > idx && x.prepareForCascadeRemove());

            sheets.forEach((sheet: Sheet) => {
                sheet.setNoAnimation(true);
                sheet.setTriggerClose(false);
                sheet.remove();
            });

        }
    }

    getAllSheetsWithChanges = () => {
        const changes: Array<{ sheetId: string; index: number }> = [];

        for (let i = 0; i < this._sheets.length; i++) {
            if (this._sheets[i].hasUnsavedChanges()) {
                changes.push({ index: i, sheetId: this._sheets[i].id() });
            }
        }

        return changes;
    }

}